import { useMemo } from "react"

import { RUNS_VIEW_MODE, type RunsViewMode } from "../types/viewMode"
import {
  useRunsInfiniteQuery,
  type RunsKeysetFilters,
} from "./useRunsInfiniteQuery"

/** Coordinates table rows, optional summary detail, and independent filter catalogs. */
export function useRunsPageQueries({
  viewMode,
  filters,
  enabled = true,
  includeSummarySimulations = true,
}: {
  viewMode: RunsViewMode
  filters: RunsKeysetFilters
  enabled?: boolean
  includeSummarySimulations?: boolean
}) {
  // ── Tier-1: table data ─────────────────────────────────────────────────────
  const tableQuery = useRunsInfiniteQuery({ viewMode, filters, enabled })
  const summarySimulationsQuery = useRunsInfiniteQuery({
    viewMode: RUNS_VIEW_MODE.SIMULATIONS,
    filters,
    enabled:
      includeSummarySimulations &&
      enabled &&
      viewMode !== RUNS_VIEW_MODE.SIMULATIONS,
  })

  const tableHasInitiallyLoaded = tableQuery.hasSettledInitialFetch
  const summarySimulations =
    viewMode === RUNS_VIEW_MODE.SIMULATIONS
      ? tableQuery.allSimulations
      : summarySimulationsQuery.allSimulations
  const summarySimulationsIsLoading =
    viewMode !== RUNS_VIEW_MODE.SIMULATIONS &&
    includeSummarySimulations &&
    enabled &&
    (summarySimulationsQuery.isLoading ||
      (summarySimulationsQuery.isFetching &&
        summarySimulationsQuery.allSimulations.length === 0))

  const referenceRows = useMemo(
    () => [
      ...tableQuery.allRuns,
      ...tableQuery.allSimulations,
      ...(includeSummarySimulations && viewMode !== RUNS_VIEW_MODE.SIMULATIONS
        ? summarySimulations
        : []),
    ],
    [
      tableQuery.allRuns,
      tableQuery.allSimulations,
      summarySimulations,
      includeSummarySimulations,
      viewMode,
    ],
  )

  return {
    referenceRows,
    // Tier-1 — passed through unmodified so callers work exactly as before
    allRuns: tableQuery.allRuns,
    allSimulations: tableQuery.allSimulations,
    summarySimulations,
    summarySimulationsIsLoading,
    loadedCursors: tableQuery.loadedCursors,
    hasNextPage: tableQuery.hasNextPage,
    fetchNextPage: tableQuery.fetchNextPage,
    isFetchingNextPage: tableQuery.isFetchingNextPage,
    isError: tableQuery.isError,
    error: tableQuery.error,
    filterQueriesEnabled: enabled,
    isLoading: tableQuery.isLoading,
    isFetching: tableQuery.isFetching,
    isPlaceholderData: tableQuery.isPlaceholderData,
    refetch: tableQuery.refetch,

    // Summary readiness — filter catalogs use filterQueriesEnabled independently
    tableHasInitiallyLoaded,
  }
}
